import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import * as adminService from '../services/adminService';

const PractitionerPaymentManager = ({ clinicId }) => {
  const [practitioners, setPractitioners] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    practitioner_id: '',
    payment_account_id: ''
  });

  useEffect(() => {
    if (clinicId) {
      fetchData();
    }
  }, [clinicId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all required data in parallel
      const [practitionersResponse, paymentAccountsResponse, assignmentsResponse] = await Promise.all([
        adminService.getPractitioners(clinicId),
        fetch('/api/admin/payment-accounts'),
        fetch(`/api/admin/payment-accounts/assignments/${clinicId}`)
      ]);

      // adminService.getPractitioners returns the practitioners array directly
      if (!Array.isArray(practitionersResponse)) {
        throw new Error('Failed to fetch practitioners');
      }

      if (!paymentAccountsResponse.ok) {
        throw new Error('Failed to fetch payment accounts');
      }

      if (!assignmentsResponse.ok) {
        throw new Error('Failed to fetch assignments');
      }

      const practitioners = practitionersResponse;
      const paymentAccounts = await paymentAccountsResponse.json();
      const assignments = await assignmentsResponse.json();

      console.log('PractitionerPaymentManager - Debug data:');
      console.log('- Practitioners:', practitioners);
      console.log('- Payment Accounts:', paymentAccounts);
      console.log('- Assignments:', assignments);

      setPractitioners(practitioners);
      setPaymentAccounts(paymentAccounts.filter(account => account.is_active));
      setAssignments(assignments);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setFormData({
      practitioner_id: '',
      payment_account_id: ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      const response = await fetch('/api/admin/payment-accounts/assignments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          clinic_id: clinicId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create assignment');
      }

      await fetchData();
      handleClose();
      setSnackbar({
        open: true,
        message: 'Payment account assigned successfully',
        severity: 'success'
      });
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (practitionerId) => {
    if (!window.confirm('Are you sure you want to remove this payment account assignment?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/admin/payment-accounts/assignments/${practitionerId}/${clinicId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove assignment');
      }

      await fetchData();
      setSnackbar({
        open: true,
        message: 'Assignment removed successfully',
        severity: 'success'
      });
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const getPractitionerName = (practitionerId) => {
    const practitioner = practitioners.find(p => p.id === practitionerId);
    return practitioner ? `${practitioner.title || ''} ${practitioner.name || 'Unknown'}`.trim() : 'Unknown Practitioner';
  };

  // Memoize these functions to prevent infinite re-renders
  const assignedPractitionerIds = React.useMemo(() => {
    return assignments.map(assignment => assignment.practitioner_id);
  }, [assignments]);

  const unassignedPractitioners = React.useMemo(() => {
    // Ensure practitioners is an array before filtering
    if (!Array.isArray(practitioners) || practitioners.length === 0) {
      console.log('PractitionerPaymentManager - No practitioners loaded yet');
      return [];
    }
    
    const unassigned = practitioners.filter(p => !assignedPractitionerIds.includes(p.id));
    console.log('PractitionerPaymentManager - Filtering practitioners:');
    console.log('- All practitioners:', practitioners.length);
    console.log('- Assigned IDs:', assignedPractitionerIds.length);
    console.log('- Unassigned practitioners:', unassigned.length);
    console.log('- Sample unassigned:', unassigned.map(p => `${p.title || ''} ${p.name || 'Unknown'}`));
    return unassigned;
  }, [practitioners, assignedPractitionerIds]);

  if (!clinicId) {
    return (
      <Container maxWidth="lg">
        <Alert severity="info">
          Please select a clinic first to manage practitioner payment accounts.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Practitioner Payment Accounts
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Assign specific payment accounts to practitioners. When assigned, bookings with that practitioner will use their designated payment processor instead of the clinic default.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">{practitioners.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Practitioners
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PaymentIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h6">{assignments.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Assigned Accounts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6">{paymentAccounts.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Available Accounts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          sx={{ mb: 3 }}
          disabled={unassignedPractitioners.length === 0 || paymentAccounts.length === 0}
        >
          Assign Payment Account
        </Button>

        {unassignedPractitioners.length === 0 && practitioners.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            All practitioners have been assigned payment accounts.
          </Alert>
        )}

        {paymentAccounts.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No payment accounts available. Please create payment accounts first.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Practitioner</TableCell>
                  <TableCell>Payment Account</TableCell>
                  <TableCell>Processor Type</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment) => (
                  <TableRow key={assignment.practitioner_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2">
                          {getPractitionerName(assignment.practitioner_id)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {assignment.paymentAccount.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={assignment.paymentAccount.processor_type}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(assignment.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleDelete(assignment.practitioner_id)}
                        color="error"
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {assignments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No payment account assignments found. Assign payment accounts to practitioners to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Assignment Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            Assign Payment Account to Practitioner
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Practitioner</InputLabel>
                  <Select
                    name="practitioner_id"
                    value={formData.practitioner_id}
                    onChange={handleChange}
                    label="Practitioner"
                  >
                    {loading ? (
                      <MenuItem disabled>Loading practitioners...</MenuItem>
                    ) : unassignedPractitioners.length === 0 ? (
                      <MenuItem disabled>No available practitioners</MenuItem>
                    ) : (
                      unassignedPractitioners.map((practitioner) => (
                        <MenuItem key={practitioner.id} value={practitioner.id}>
                          {practitioner.title} {practitioner.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Account</InputLabel>
                  <Select
                    name="payment_account_id"
                    value={formData.payment_account_id}
                    onChange={handleChange}
                    label="Payment Account"
                  >
                    {paymentAccounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name} ({account.processor_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || !formData.practitioner_id || !formData.payment_account_id}
            >
              {loading ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PractitionerPaymentManager;